/**
 * Dictar en vez de escribir.
 *
 * Graba, manda el audio al backend de esta app, y devuelve el texto. Nada más:
 * el audio no se guarda en la tablet, no se reproduce, no se descarga y no
 * queda en ninguna variable después de mandarlo.
 *
 * Es el único sitio de la app donde sale audio del dispositivo, y sale hacia el
 * servidor de la casa y de ahí al ordenador de casa. La pantalla que lo usa lo
 * dice antes de que nadie pulse nada, porque es un paso más grande que mandar
 * texto: una voz describiendo a unos niños no es lo mismo que una frase escrita.
 *
 * Lo que vuelve es un borrador, no un resultado. Whisper falla con los nombres
 * —«querría» sale «querida», «dejara» sale «dejera»— y los nombres son justo lo
 * que el vocabulario de propuestas casa de forma exacta. Por eso el texto va a
 * un campo editable y no directo al ayudante.
 */

export type DictadoFailure =
  'sin-permiso' | 'sin-microfono' | 'sin-conexion' | 'demasiado-largo' | 'no-se-entendio'

export interface Grabacion {
  /** Corta la grabación y devuelve lo que se haya entendido. */
  stop(): Promise<{ ok: true; texto: string } | { ok: false; failure: DictadoFailure }>
  /** Tira la grabación sin mandar nada. */
  cancel(): void
}

export type DictadoStart =
  | { readonly ok: true; readonly grabacion: Grabacion }
  | { readonly ok: false; readonly failure: DictadoFailure }

/** Dos minutos. Más que eso no es dictar una idea, es dejarse el micro abierto. */
export const MAX_SEGUNDOS = 120

function mejorTipo(): string {
  const candidatos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
  for (const tipo of candidatos) {
    if (MediaRecorder.isTypeSupported(tipo)) return tipo
  }
  return ''
}

export async function startDictado(
  apiUrl: string,
  token: string | undefined,
): Promise<DictadoStart> {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    return { ok: false, failure: 'sin-microfono' }
  }

  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch {
    return { ok: false, failure: 'sin-permiso' }
  }

  const tipo = mejorTipo()
  const recorder = new MediaRecorder(stream, tipo.length > 0 ? { mimeType: tipo } : undefined)
  const trozos: Blob[] = []
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) trozos.push(event.data)
  })

  const soltar = (): void => {
    for (const track of stream.getTracks()) track.stop()
    trozos.length = 0
  }

  const parado = new Promise<void>((resolve) => {
    recorder.addEventListener('stop', () => {
      resolve()
    })
  })

  recorder.start()

  return {
    ok: true,
    grabacion: {
      cancel() {
        if (recorder.state !== 'inactive') recorder.stop()
        soltar()
      },
      async stop() {
        if (recorder.state !== 'inactive') recorder.stop()
        await parado
        const audio = new Blob(trozos, { type: recorder.mimeType })
        for (const track of stream.getTracks()) track.stop()
        // El array se vacía antes de la subida: lo que viaja es este Blob y
        // nada más, y cuando termine no quedará referencia a él.
        trozos.length = 0

        if (audio.size === 0) return { ok: false as const, failure: 'no-se-entendio' as const }

        try {
          const response = await fetch(`${apiUrl.replace(/\/+$/, '')}/ia/voz`, {
            method: 'POST',
            headers: {
              'Content-Type': audio.type || 'application/octet-stream',
              ...(token !== undefined && token.length > 0
                ? { Authorization: `Bearer ${token}` }
                : {}),
            },
            body: audio,
          })

          if (response.status === 413) {
            return { ok: false as const, failure: 'demasiado-largo' as const }
          }
          if (!response.ok) return { ok: false as const, failure: 'sin-conexion' as const }

          const cuerpo = (await response.json()) as { texto?: unknown }
          const texto = typeof cuerpo.texto === 'string' ? cuerpo.texto.trim() : ''
          if (texto.length === 0) {
            return { ok: false as const, failure: 'no-se-entendio' as const }
          }
          return { ok: true as const, texto }
        } catch {
          return { ok: false as const, failure: 'sin-conexion' as const }
        }
      },
    },
  }
}
